import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { MAP_EVENTS_AND_UNITS_SEARCH_INPUT_DEBOUNCE_THRESHOLD } from '../configuration';
import { useEsriMapEventAndUnitSearch } from '../hooks/use-esri-map-event-and-unit-search';
import { useEsriMapStore } from '../hooks/use-esri-map-store';
import { MapLedgerEventView, MapLedgerUnitView } from '@mark43/cad-api';
import { EventAndUnitSearchSelect } from './event-and-unit-search-select';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { getUnitUserLocation } from '../../esri/helpers/locations';

interface Props {
    selectUnit: (unitId: number, shouldResetZoomLevel?: boolean) => void;
    selectEvent: (eventId: number, shouldResetZoomLevel?: boolean) => void;
    selectUnitUser: (unitUserId: number, shouldResetZoomLevel?: boolean) => void;
}

export const EventAndUnitSearchInput = observer(function EventAndUnitSearchInput({
    selectUnit,
    selectEvent,
    selectUnitUser,
}: Props): React.ReactElement {
    const mapStore = useEsriMapStore();
    const {
        hasSearchInitialized,
        initEventsAndUnits,
        queryEventsAndUnits,
        clearSearchInitialization,
    } = useEsriMapEventAndUnitSearch();
    const [unitsSearchResult, setUnitsSearchResult] = useState<MapLedgerUnitView[]>([]);
    const [eventsSearchResult, setEventsSearchResult] = useState<MapLedgerEventView[]>([]);
    const [usersSearchResult, setUsersSearchResult] = useState<MapLedgerUnitView[]>([]);

    const mapUnitAssignedUsers = useMemo(() => {
        const mapViewportUnits = mapStore.mapViewportUnits ?? [];
        return mapViewportUnits.flatMap((mapUnit) => {
            const locations = mapUnit.unitUserLocations ?? [];
            return locations
                .filter((loc) => loc.entityId)
                .map((loc) => {
                    const gpsLocation = getUnitUserLocation(loc);
                    if (gpsLocation && gpsLocation.entityId && gpsLocation.latitude !== undefined) {
                        return {
                            ...mapUnit,
                            visible: true,
                            entityId: gpsLocation.entityId,
                            type: EsriMapEntityTypeEnum.USER,
                            isLocationPotentiallyInaccurate: loc.isLocationPotentiallyInaccurate,
                            gpsLocation,
                            geoLocation: undefined,
                            unitUserLocations: [],
                            personnel: loc.userNameDisplay ?? mapUnit.personnelName,
                        };
                    }
                    return null;
                })
                .filter(Boolean);
        });
    }, [mapStore.mapViewportUnits]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearch = useCallback(
        debounce((keyword: string) => {
            if (keyword) {
                // onBlur will clear the search initialization, so the next input will based on latest mapStore data.
                if (!hasSearchInitialized()) {
                    initEventsAndUnits({
                        mapLedgerEvents: mapStore.mapLedgerEvents,
                        mapLedgerUnits: mapStore.mapLedgerUnits,
                        mapLedgerUnitUsers: mapUnitAssignedUsers,
                    });
                }
                const results = queryEventsAndUnits(keyword);
                if (results) {
                    const { mapLedgerEvents, mapLedgerUnits, mapLedgerUnitUsers } = results;
                    setUnitsSearchResult(mapLedgerUnits);
                    setEventsSearchResult(mapLedgerEvents);
                    setUsersSearchResult(mapLedgerUnitUsers);
                }
            }
        }, MAP_EVENTS_AND_UNITS_SEARCH_INPUT_DEBOUNCE_THRESHOLD),
        [initEventsAndUnits, mapStore.mapLedgerEvents, mapStore.mapLedgerUnits, queryEventsAndUnits, mapUnitAssignedUsers]
    );

    return (
        <EventAndUnitSearchSelect
            onChange={handleSearch}
            onBlur={clearSearchInitialization}
            unitsSearchResult={unitsSearchResult}
            eventsSearchResult={eventsSearchResult}
            usersSearchResult={usersSearchResult}
            selectUnit={selectUnit}
            selectEvent={selectEvent}
            selectUnitUser={selectUnitUser}
        />
    );
});
